import React from 'react';
import { evaluateCondition, getAlertState } from './utils/alertUtils';
import { formatValue, formatValueWithDecimals } from '../../utils/numberFormatter';
import { validateBadgeValue, debugChartData } from '../../utils/chartDataValidator';
import { getStyleValue } from '../../utils/styleMigrationHelper';

/**
 * BadgeRenderer 컴포넌트
 * 값을 fancy한 배지 스타일로 표시하는 컴포넌트
 * 
 * @param {Object} chartData - 차트 데이터 (표준 구조)
 * @param {string} [chartData.viz_type] - 'badge'
 * @param {Object} [chartData.data] - 데이터
 * @param {Array} [chartData.data.series] - 시리즈 데이터
 * @param {Object} [chartData.options] - 옵션
 * @param {boolean} [chartData.isLoading] - 로딩 상태
 * @param {Object} error - 에러 객체
 */
const BadgeRenderer = ({ chartData, error, onChartClick, isEditMode, componentId, onEmergencyTrigger, isClicked, isSelected = false }) => {

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        color: '#e74c3c',
        fontSize: '14px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <strong>배지 오류:</strong> {error.message || String(error)}
      </div>
    );
  }

  const options = chartData?.options || {};
  // transparent 옵션 처리 (boolean true 또는 string "true" 모두 허용)
  const isTransparent = options.transparent === true || options.transparent === 'true';
  const isLoading = chartData?.isLoading || !chartData?.data;
  
  // 디버깅: visibility 옵션 확인
  const visibilityTitle = options.visibility?.title ?? options.style?.visibility?.title;

  // 로딩 상태 UI
  if (isLoading || !chartData || !chartData.data || !chartData.data.series || chartData.data.series.length === 0) {
    const title = options.title || '로딩 중...';
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: '12px',
        backgroundColor: isTransparent ? 'transparent' : '#f3f4f6',
        borderRadius: '20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>{title}</div>
        <div className="badge-loading-shimmer" style={{ width: '60px', height: '20px', backgroundColor: '#e0e0e0', borderRadius: '4px' }} />
      </div>
    );
  }

  const series = chartData.data.series[0];
  const rawValue = Array.isArray(series.values) ? series.values[0] : series.values;

  // 포인트 메타(추가 컬럼/원본 행) 추출
  const rawIsObject = typeof rawValue === 'object' && rawValue !== null;
  const rawInnerValue = rawIsObject && Object.prototype.hasOwnProperty.call(rawValue, 'value')
    ? rawValue.value
    : rawValue;
  const extrasArray = rawIsObject && Array.isArray(rawValue.__extras) ? rawValue.__extras : [];
  const originalRow = rawIsObject && rawValue.__row && typeof rawValue.__row === 'object' ? rawValue.__row : undefined;

  // 값 검증 및 정규화 (객체면 내부 value를 사용)
  const value = validateBadgeValue(rawInnerValue, {
    defaultValue: options.defaultValue || 'N/A',
    allowNull: false
  });
  
  // 디버그 모드에서 차트 데이터 로깅
  if (componentId && process.env.NODE_ENV === 'development') {
    debugChartData(componentId, chartData);
  }
  
  const label = series.name || '';
  
  const style = options.style || {};
  const advanced = options.advanced || {};
  const chartType = 'badge'; // 차트 타입 명시
  
  // 공통 스타일 추출
  const commonFonts = style.fonts || {};
  const commonBackground = style.background || {};
  const { alertCondition, criticalCondition, emergencyCondition } = { ...options, ...advanced };
  const alertState = getAlertState(value, { alertCondition, criticalCondition, emergencyCondition });
  
  // 디버깅: 배지 알람 상태 확인
  if (componentId && componentId.includes('current_people')) {
  }
  
  if (alertState === 'emergency' && typeof onEmergencyTrigger === 'function') {
    onEmergencyTrigger({
      chartId: componentId,
      chartTitle: options.title || '알 수 없는 차트',
      condition: emergencyCondition,
      value: value,
      timestamp: new Date().toISOString()
    });
  }
  // 통합된 스타일 값 가져오기 (레거시 폴백 포함)
  const unit = options.unit || style.unit || advanced.unit || '';
  const badgeStyle = getStyleValue(options, chartType, 'badgeStyle') || 'rounded';

  const getEffectiveBadgeColor = () => {
    switch (alertState) {
      case 'emergency': return '#8B0000';
      case 'critical': return '#DC3545';
      case 'warning': return '#FFC107';
      default: return getStyleValue(options, chartType, 'badgeColor') || style.colors?.[0] || '#3498db';
    }
  };
  
  // 색상을 밝게/어둡게 조절하는 유틸 (눌림 효과에 사용) - 사용 지점보다 위에 선언
  function lightenColor(color, percent) {
    const num = parseInt(color.replace("#", ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return "#" + (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    ).toString(16).slice(1);
  }

  // 필요한 변수들을 먼저 정의
  const showShadow = advanced.showShadow !== false;
  
  // 알람 상태별 애니메이션 스타일 생성 함수를 먼저 정의 (강화된 버전)
  const getAlertAnimationStyle = () => {
    switch (alertState) {
      case 'warning':
        return {
          animation: 'warning-enhanced 1.5s ease-in-out infinite',
          border: '2px solid #FFC107',
          transform: 'scale(1)'
        };
      case 'critical':
        return {
          animation: 'critical-flash 1.2s ease-in-out infinite',
          border: '3px solid #DC3545',
          boxShadow: `0 0 20px rgba(220, 53, 69, 0.7), ${showShadow ? '0 8px 25px rgba(0,0,0,0.15)' : 'none'}`
        };
      case 'emergency':
        return {
          animation: 'emergency-extreme 0.8s ease-in-out infinite',
          border: '4px solid #FF0000',
          boxShadow: `0 0 35px rgba(255, 0, 0, 1), ${showShadow ? '0 8px 25px rgba(0,0,0,0.15)' : 'none'}`
        };
      default:
        return {};
    }
  };

  let badgeColor = getEffectiveBadgeColor();
  let textColor = getStyleValue(options, chartType, 'textColor') || commonFonts.value?.color || '#ffffff';
  let titleColor;
  let clickedBorderStyle = {};
  // 실제로 렌더에 사용되는 배경 기준색 (공통 배경색 우선)
  const baseBackgroundColor = commonBackground.color || badgeColor;
  // 눌림 상태에서 배경만 살짝 어둡게 덮어쓸 값 (텍스트는 유지)
  let pressedBackgroundOverride = null;
  // 선택 상태 스타일(표준 위치: options.style.selected)
  const selectedStyle = style.selected || {};
  const badgeBgSelected = selectedStyle.badgeBgSelected || null; // 없으면 내부 계산 사용
  const badgeTint = selectedStyle.badgeTint || null; // 추가 틴트(예: 'rgba(74,144,226,0.12)')
  
  const pressed = isClicked || isSelected; // 선택/클릭 둘 다 눌림 상태로 간주
  if (pressed) {
    // 알람 상태인 경우: 색상 반전 대신 굵은 테두리로 선택 표시
    if (alertState !== 'default') {
      clickedBorderStyle = {
        border: '6px solid #FFD700',
        boxShadow: `0 0 0 3px #000000, ${getAlertAnimationStyle().boxShadow || 'none'}`
      };
    }
    // 텍스트 색상은 유지하고, 배경은 selected 스타일 우선 적용
    pressedBackgroundOverride = badgeBgSelected ? badgeBgSelected : lightenColor(baseBackgroundColor, -16);
    titleColor = textColor;
  } else {
    // 클릭되지 않은 상태에서는 기본 titleColor 계산 로직 사용
    titleColor = getStyleValue(options, chartType, 'titleColor') || commonFonts.title?.color || textColor;
  }

  // 통합된 스타일 헬퍼 사용
  // 우선순위: style.fonts > 레거시 경로들 > 기본값
  const valueFontSize = getStyleValue(options, chartType, 'fontSize') || commonFonts.value?.fontSize || 48;
  const labelFontSize = commonFonts.label?.fontSize || options.detailFontSize || style.detailFontSize || options.labelFontSize || options.advanced?.detailFontSize || options.advanced?.labelFontSize || 16;
  const unitFontSize = getStyleValue(options, chartType, 'unitFontSize') || commonFonts.unit?.fontSize || valueFontSize * 0.5;
  const titleFontSize = getStyleValue(options, chartType, 'titleFontSize') || commonFonts.title?.fontSize || 24;

  // CSS 클래스 대신 인라인 애니메이션 스타일 사용
  
  const showGlow = advanced.showGlow || false;
  const glowColor = advanced.glowColor || badgeColor;

  // 소수점 자리수 옵션 적용
  const decimalPlaces = advanced.decimalPlaces ?? null;
  const formattedValue = formatValueWithDecimals(value, decimalPlaces);
  
  const getShapeStyle = () => {
    switch (badgeStyle) {
      case 'square': return { borderRadius: '0' };
      case 'circle': return { borderRadius: '50%' };
      case 'hexagon': return { clipPath: 'polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%)' };
      default: return { borderRadius: '20px' };
    }
  };
  
  // titleColor 계산을 위로 이동하여 중복 제거

  const alertAnimationStyle = getAlertAnimationStyle();
  
  // 정렬 옵션(좌/우/가운데) 처리: style.textAlign 또는 options.textAlign
  const contentTextAlign = (style.textAlign || options.textAlign || 'center');
  const horizontalAlign = contentTextAlign === 'right' ? 'flex-end' : (contentTextAlign === 'left' ? 'flex-start' : 'center');

  const badgeCoreStyle = {
    width: '100%',
    height: '100%',
    padding: '12px',
    // 클릭된 상태일 때는 투명 설정을 무시하고 반전된 배경색 적용
    // background: isClicked && alertState === 'default' ? badgeColor : (isTransparent ? 'transparent' : (commonBackground.color || badgeColor)), // 공통 배경색 우선 적용
    // transparent=true인 경우 배지 내부 배경도 투명 처리
    background: isTransparent ? 'transparent' : (pressedBackgroundOverride || baseBackgroundColor), // 공통 배경색 우선 적용 + 눌림시 덮어쓰기
    color: textColor,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: horizontalAlign,
    position: 'relative',
    transition: 'all 0.15s ease',
    boxShadow: pressed
      ? 'inset 0 3px 8px rgba(0,0,0,0.25), 0 2px 6px rgba(0,0,0,0.12)'
      : (alertState === 'default' ? (showShadow ? `0 8px 25px rgba(0,0,0,0.15)` : 'none') : 'none'),
    transform: pressed ? 'translateY(1.5px) scale(0.985)' : 'none',
    cursor: !isEditMode && onChartClick ? 'pointer' : 'default',
    borderRadius: commonBackground.borderRadius !== undefined ? `${commonBackground.borderRadius}px` : undefined, // 공통 borderRadius 적용
    ...getShapeStyle(),
    ...alertAnimationStyle,
    // 클릭된 상태일 때 테두리 오버라이드 (알람 애니메이션 유지)
    ...clickedBorderStyle
  };

  if (showGlow) {
    badgeCoreStyle.filter = `drop-shadow(0 0 15px ${glowColor}60)`;
  }

  const handleBadgeClick = () => {
    if (!isEditMode && typeof onChartClick === 'function') {
      onChartClick({
        value,
        seriesName: label,
        componentType: 'badge',
        // 라벨/제목 전달 (정규화기: badge.label, badge.title)
        label: label || options.title,
        title: options.title,
        // 확장: 추가 컬럼과 원본 행 전달 (정규화기에서 extra1/extra2/row로 매핑)
        extra1: extrasArray[0],
        extra2: extrasArray[1],
        row: originalRow
      }, 'self');
    }
  };

  return (
    <div style={{ width: '100%', height: '100%', boxSizing: 'border-box', position: 'relative' }} onClick={handleBadgeClick}>
      <div style={badgeCoreStyle}>
        {/* 선택 시 자연스러운 눌림 그라데이션 오버레이 */}
        {pressed && (
          <div style={{
            position: 'absolute',
            inset: 0,
            borderRadius: commonBackground.borderRadius !== undefined ? `${commonBackground.borderRadius}px` : getShapeStyle().borderRadius,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.06), rgba(255,255,255,0.04))',
            mixBlendMode: 'multiply',
            pointerEvents: 'none'
          }}/>
        )}
        {options.showTitle !== false && options.title && (
          <div style={{
            fontSize: commonFonts.title?.fontSize ? `${commonFonts.title.fontSize}px` : `${titleFontSize}px`,
            fontWeight: commonFonts.title?.fontWeight || '600',
            fontStyle: commonFonts.title?.fontStyle || 'normal',
            color: titleColor || commonFonts.title?.color,
            opacity: pressed ? 1.0 : 0.85, // 선택/클릭 시 더 선명하게
            position: 'absolute',
            top: '12px',
            left: '15px',
            fontFamily: commonFonts.title?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            lineHeight: commonFonts.title?.lineHeight
          }}>
            {options.title}
          </div>
        )}
        <div style={{
          fontSize: commonFonts.value?.fontSize ? `${commonFonts.value.fontSize}px` : `${valueFontSize}px`,
          fontWeight: commonFonts.value?.fontWeight || 'bold',
          fontStyle: commonFonts.value?.fontStyle || 'normal',
          color: textColor || commonFonts.value?.color,
          lineHeight: commonFonts.value?.lineHeight || 1.1,
          fontFamily: commonFonts.value?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          textShadow: '0 1px 3px rgba(0,0,0,0.1)',
          width: '100%',
          textAlign: contentTextAlign
        }}>
          {formattedValue}
          {unit && (
            <span style={{
              fontSize: commonFonts.unit?.fontSize ? `${commonFonts.unit.fontSize}px` : `${unitFontSize}px`,
              fontWeight: commonFonts.unit?.fontWeight || '500',
              fontStyle: commonFonts.unit?.fontStyle || 'normal',
              color: commonFonts.unit?.color || textColor,
              marginLeft: '5px',
              opacity: 0.9,
              fontFamily: commonFonts.unit?.fontFamily || commonFonts.value?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
            }}>
              {unit}
            </span>
          )}
        </div>
      </div>
      {pressed && (
        <div style={{
          position: 'absolute',
          inset: 0,
          borderRadius: commonBackground.borderRadius !== undefined ? `${commonBackground.borderRadius}px` : getShapeStyle().borderRadius,
          background: badgeTint || 'rgba(74, 144, 226, 0.12)',
          pointerEvents: 'none'
        }} />
      )}
      {pressed && (
        <div style={{
          position: 'absolute',
          inset: '-2px',
          borderRadius: commonBackground.borderRadius !== undefined ? `${commonBackground.borderRadius + 2}px` : undefined,
          border: '2px solid rgba(74, 144, 226, 0.85)',
          boxShadow: '0 0 12px rgba(74, 144, 226, 0.35)',
          pointerEvents: 'none'
        }}/>
      )}
      <style>{`
        .badge-loading-shimmer {
          animation: shimmer 1.5s infinite linear;
          background: linear-gradient(to right, #e0e0e0 4%, #f0f0f0 25%, #e0e0e0 36%);
          background-size: 1000px 100%;
        }
        @keyframes shimmer { 
          0% { background-position: -1000px 0; } 
          100% { background-position: 1000px 0; } 
        }
        
        /* 주의 단계: 색상 변화 + 강한 펄스 */
        @keyframes warning-enhanced { 
          0%, 100% { 
            transform: scale(1); 
            background-color: #FFC107;
            box-shadow: 0 0 10px rgba(255, 193, 7, 0.4);
          } 
          50% { 
            transform: scale(1.08); 
            background-color: #FFB300;
            box-shadow: 0 0 20px rgba(255, 193, 7, 0.8);
          } 
        }
        
        /* 경고 단계: 강한 깜빡임 + 색상 변화 */
        @keyframes critical-flash { 
          0%, 100% { 
            transform: scale(1); 
            background-color: #DC3545;
            box-shadow: 0 0 20px rgba(220, 53, 69, 0.7);
            opacity: 1;
          } 
          25% { 
            transform: scale(1.06); 
            background-color: #E74C3C;
            box-shadow: 0 0 30px rgba(220, 53, 69, 1);
            opacity: 0.8;
          }
          50% { 
            transform: scale(1.1); 
            background-color: #C82333;
            box-shadow: 0 0 40px rgba(220, 53, 69, 1);
            opacity: 1;
          }
          75% { 
            transform: scale(1.06); 
            background-color: #E74C3C;
            box-shadow: 0 0 30px rgba(220, 53, 69, 1);
            opacity: 0.9;
          }
        }
        
        /* 긴급 단계: 강한 진동 + 깜빡임 + 색상 번갈아가며 변화 */
        @keyframes emergency-extreme { 
          0% { 
            transform: scale(1) translateX(0px); 
            background-color: #8B0000;
            box-shadow: 0 0 35px rgba(255, 0, 0, 1);
            opacity: 1;
          } 
          12.5% { 
            transform: scale(1.15) translateX(-3px); 
            background-color: #FF0000;
            box-shadow: 0 0 50px rgba(255, 0, 0, 1);
            opacity: 0.7;
          }
          25% { 
            transform: scale(1.2) translateX(3px); 
            background-color: #DC143C;
            box-shadow: 0 0 60px rgba(255, 0, 0, 1);
            opacity: 1;
          }
          37.5% { 
            transform: scale(1.15) translateX(-2px); 
            background-color: #FF4500;
            box-shadow: 0 0 50px rgba(255, 69, 0, 1);
            opacity: 0.8;
          }
          50% { 
            transform: scale(1.25) translateX(2px); 
            background-color: #FF0000;
            box-shadow: 0 0 70px rgba(255, 0, 0, 1);
            opacity: 0.6;
          }
          62.5% { 
            transform: scale(1.15) translateX(-3px); 
            background-color: #8B0000;
            box-shadow: 0 0 50px rgba(139, 0, 0, 1);
            opacity: 0.9;
          }
          75% { 
            transform: scale(1.1) translateX(3px); 
            background-color: #B22222;
            box-shadow: 0 0 45px rgba(178, 34, 34, 1);
            opacity: 1;
          }
          87.5% { 
            transform: scale(1.05) translateX(-1px); 
            background-color: #FF0000;
            box-shadow: 0 0 40px rgba(255, 0, 0, 1);
            opacity: 0.8;
          }
          100% { 
            transform: scale(1) translateX(0px); 
            background-color: #8B0000;
            box-shadow: 0 0 35px rgba(255, 0, 0, 1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default BadgeRenderer;